import { ArrowRight, BookOpenIcon } from 'lucide-react'
import PropTypes from 'prop-types'
import { Link, useParams } from 'react-router'

import rescueXIcon from '@/assets/images/icon.png'
import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import { Skeleton } from '@/components/elements/skeleton'
import { TextLink } from '@/components/TextLink'
import { getNextActionableCourseRecord } from '@/utils/certificates'

// One-line summary of where the student is in a class — module progress, or a
// hands-on prompt once the online coursework is done.
function classSummary(studentCertificate) {
  const records = studentCertificate.courseRecords ?? []
  const total = records.length
  const completed = records.filter((r) => r.status === 'Completed').length
  const courseworkDone = total > 0 && completed === total
  const handsOnPending =
    studentCertificate.trainingStatus !== null && studentCertificate.trainingStatus !== 'Completed'

  if (courseworkDone && handsOnPending) return 'Hands-on training pending'
  if (total > 0) return `${completed} of ${total} ${total === 1 ? 'module' : 'modules'} complete`
  return studentCertificate.status
}

function CurrentClassesSection({ currentClasses, isLoading }) {
  const { entityId } = useParams()

  const heading = (
    <h2 className="flex items-center gap-2 uppercase">
      <BookOpenIcon className="h-5 w-5" />
      Current Classes
    </h2>
  )

  if (isLoading) {
    return (
      <section>
        <div className="mb-4">{heading}</div>
        <Card>
          <CardContent className="space-y-3 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </section>
    )
  }

  if (currentClasses.length === 0) {
    return (
      <section>
        <div className="mb-4">{heading}</div>
        <Card>
          <CardContent className="py-8 text-center">
            <BookOpenIcon className="text-muted-foreground/50 mx-auto mb-3 h-10 w-10" />
            <h3 className="mb-1 text-base font-semibold">No Classes in Progress</h3>
            <p className="text-muted-foreground text-sm">
              Your assigned courses and certifications will appear here once you&apos;re enrolled.
            </p>
          </CardContent>
        </Card>
      </section>
    )
  }

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        {heading}
        <TextLink to={`/accounts/${entityId}/students/courses`}>View All</TextLink>
      </div>
      <Card>
        <CardContent>
          <ul className="space-y-3">
            {currentClasses.map((studentCertificate) => {
              const nextActionable = getNextActionableCourseRecord(studentCertificate)
              const isRescueX =
                studentCertificate.certificate?.certificateType !== 'Custom Student Course'
              return (
                <li
                  key={studentCertificate.id}
                  className="flex items-center justify-between gap-3 border-b pb-2 last:border-b-0"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="flex items-center gap-1.5 font-medium">
                      {isRescueX && (
                        <img
                          src={rescueXIcon}
                          alt="RescueX"
                          title="RescueX course"
                          className="h-4 w-4 shrink-0"
                        />
                      )}
                      <span className="truncate">{studentCertificate.certificate?.name}</span>
                    </span>
                    <span className="text-muted-foreground text-sm">
                      {classSummary(studentCertificate)}
                    </span>
                  </div>
                  {nextActionable && (
                    <Button asChild size="sm" variant="outline" className="shrink-0">
                      <Link to={`/accounts/${entityId}/students/courses/${nextActionable.id}`}>
                        {nextActionable.status === 'In Progress' ? 'Resume' : 'Start'}
                        <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                      </Link>
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        </CardContent>
      </Card>
    </section>
  )
}

CurrentClassesSection.propTypes = {
  currentClasses: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
}

CurrentClassesSection.defaultProps = {
  isLoading: false,
}

export default CurrentClassesSection
